import React, { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import KaliteGecmeyenCell from "../components/KaliteGecmeyenCell";
import { damga } from "../lib/stamp";
import { Printer, Download } from "lucide-react";

const TCFG = {
  gecti: { label: "Termini Geçen", row: "border-l-4 border-red-500 bg-red-50", badge: "bg-red-600 text-white" },
  yaklasiyor: { label: "Termini Yaklaşan", row: "border-l-4 border-green-500 bg-green-50", badge: "bg-green-600 text-white" },
  uzak: { label: "Termini Uzak", row: "border-l-4 border-sky-500 bg-sky-50", badge: "bg-sky-600 text-white" },
};

const gunLabel = (g) => {
  if (g === null || g === undefined) return "—";
  if (g < 0) return `${Math.abs(g)} gün gecikti`;
  if (g === 0) return "Bugün";
  return `${g} gün kaldı`;
};

const trTarih = (s) => {
  if (!s) return "";
  const p = String(s).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : s;
};

const COLS = [
  { label: "Termin", get: (o) => trTarih(o.termin_tarihi) },
  { label: "Durum", get: (o) => (TCFG[o.termin_durum] || {}).label || "" },
  { label: "Sipariş Tarihi", get: (o) => trTarih(o.siparis_tarihi) },
  { label: "Müşteri", get: (o) => o.musteri || "" },
  { label: "Ürün Kodu", get: (o) => o.urun_kodu || "" },
  { label: "Sipariş", get: (o) => o.siparis_adeti, num: true },
  { label: "Kalan", get: (o) => o.kalan, num: true },
  { label: "Kaliteden Geçmeyen Ürünler", get: (o) => o.kaliteden_gecmeyen ?? 0, num: true },
  { label: "Kalan Süre", get: (o) => gunLabel(o.kalan_gun) },
];

const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export default function TerminDurumu() {
  const [termin, setTermin] = useState([]);
  const [tf, setTf] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/termin").then((r) => setTermin(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const counts = {
    all: termin.length,
    gecti: termin.filter((t) => t.termin_durum === "gecti").length,
    yaklasiyor: termin.filter((t) => t.termin_durum === "yaklasiyor").length,
    uzak: termin.filter((t) => t.termin_durum === "uzak").length,
  };
  const filtered = tf === "all" ? termin : termin.filter((t) => t.termin_durum === tf);
  const tabs = [
    { id: "all", label: "Tümü", cls: "bg-zinc-800 text-white" },
    { id: "gecti", label: "Termini Geçen", cls: "bg-red-600 text-white" },
    { id: "yaklasiyor", label: "Termini Yaklaşan", cls: "bg-green-600 text-white" },
    { id: "uzak", label: "Termini Uzak / Gelmeyen", cls: "bg-sky-600 text-white" },
  ];

  const exportExcel = () => {
    const head = COLS.map((c) => `<th style="background:#18181B;color:#fff;font-weight:bold;">${esc(c.label)}</th>`).join("");
    const body = filtered.map((o) => `<tr>${COLS.map((c) => `<td>${esc(c.get(o))}</td>`).join("")}</tr>`).join("");
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><tr>${head}</tr>${body}</table></body></html>`;
    const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Termin Durumu - ${damga()}.xls`; a.click();
    URL.revokeObjectURL(url);
  };

  const printPdf = () => {
    const head = COLS.map((c) => `<th class="${c.num ? "r" : ""}">${esc(c.label)}</th>`).join("");
    const body = filtered.map((o) => `<tr>${COLS.map((c) => `<td class="${c.num ? "r" : ""}">${esc(c.get(o))}</td>`).join("")}</tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><meta charset="utf-8"><title>Termin Durumu - ${damga()}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;} h1{font-size:18px;margin:0 0 4px;} .meta{font-size:11px;color:#666;margin-bottom:12px;}
      table{width:100%;border-collapse:collapse;font-size:11px;} th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;} th{background:#18181B;color:#fff;} .r{text-align:right;}</style>
      </head><body><h1>Termin Durumu</h1><div class="meta">${new Date().toLocaleString("tr-TR")} — ${filtered.length} kayıt</div>
      <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <>
      <PageHeader title="Termin Durumu" subtitle="Gelen siparişlerin termin durumuna göre takibi"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={exportExcel} data-testid="export-btn"
              className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm">
              <Download size={15} /> Excel'e Aktar
            </button>
            <button onClick={printPdf} data-testid="pdf-btn"
              className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm">
              <Printer size={15} /> PDF / Yazdır
            </button>
          </div>
        } />
      <div className="p-6">
        <div className="flex items-center gap-1.5 flex-wrap mb-4">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTf(t.id)} data-testid={`termin-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors ${tf === t.id ? t.cls : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
              {t.label} <span className="opacity-80">({counts[t.id]})</span>
            </button>
          ))}
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 260px)" }}>
          <table className="w-full data-table" data-testid="termin-table">
            <thead>
              <tr>
                <th>Termin</th><th>Durum</th><th>Sipariş Tarihi</th><th>Müşteri</th><th>Ürün Kodu</th>
                <th className="text-right">Sipariş</th><th className="text-right">Kalan</th><th className="text-right">Kaliteden Geçmeyen Ürünler</th><th>Kalan Süre</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-10 text-zinc-400">Yükleniyor...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-zinc-400">Kayıt yok.</td></tr>
              ) : filtered.map((o) => {
                const cfg = TCFG[o.termin_durum];
                return (
                  <tr key={o.id} className={cfg.row} data-testid={`termin-row-${o.id}`}>
                    <td className="mono">{trTarih(o.termin_tarihi) || "—"}</td>
                    <td><span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${cfg.badge}`}>{cfg.label}</span></td>
                    <td className="mono">{trTarih(o.siparis_tarihi) || "—"}</td>
                    <td>{o.musteri || "—"}</td>
                    <td className="mono">{o.urun_kodu}</td>
                    <td className="num">{o.siparis_adeti}</td>
                    <td className="num font-semibold">{o.kalan}</td>
                    <td className="num"><KaliteGecmeyenCell row={o} /></td>
                    <td className="text-xs font-semibold">{gunLabel(o.kalan_gun)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-xs text-zinc-400">{filtered.length} kayıt</div>
      </div>
    </>
  );
}
